import { useEffect, type RefObject } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Atrapa el foco del teclado dentro de un contenedor (modal, diálogo, panel).
 * Mientras `active` es verdadero:
 *  - mueve el foco al primer elemento enfocable al abrir,
 *  - hace que Tab / Shift+Tab circulen dentro del contenedor,
 *  - restaura el foco al elemento que lo tenía antes al cerrar.
 *
 * El contenedor debe tener `tabIndex={-1}` para poder recibir el foco de
 * respaldo cuando no hay ningún elemento enfocable dentro.
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active = true) {
  useEffect(() => {
    if (!active) return;
    const node = ref.current;
    if (!node) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    const focusables = () =>
      Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );

    // Lleva el foco dentro del diálogo al abrir.
    (focusables()[0] ?? node).focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) {
        e.preventDefault();
        node!.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const activeEl = document.activeElement;
      if (e.shiftKey) {
        if (activeEl === first || !node!.contains(activeEl)) {
          e.preventDefault();
          last.focus();
        }
      } else if (activeEl === last || !node!.contains(activeEl)) {
        e.preventDefault();
        first.focus();
      }
    }

    node.addEventListener('keydown', onKeyDown);
    return () => {
      node.removeEventListener('keydown', onKeyDown);
      // Devuelve el foco a donde estaba (p. ej. el botón que abrió el diálogo).
      previouslyFocused?.focus?.();
    };
  }, [ref, active]);
}
